import { iconNavLixeira } from '@bsds-lib-component/icons/icons';
import { BsdsCard, BsdsCheckbox } from '@bsds-lib-component/react';
import React, { useMemo } from 'react';

import type { SelfBillingTableItem, SelfBillingTableProps } from '../../../types/vida/table/selfBillingTableType';
import {
  isSeguradoBillingActive,
  mapTipoDocumentoToLabel,
  mapTipoSeguradoToLabel,
} from '../../../utils/selfBillingUtil';
import Button from '../../button/Button';
import Pagination from '../../pagination/Pagination';
import Tooltip from '../../tooltip/Tooltip';
import TableItemActionsMenu from '../shared/TableItemActionsMenu';
import TableMenuItemButton from '../shared/TableMenuItemButton';
import { createMenuItemActionHandlers } from '../shared/tableMenuUtils';
import { useTableItemMenu } from '../shared/useTableItemMenu';
import { useTableRowSelection } from '../shared/useTableRowSelection';

const SelfBillingTable: React.FC<SelfBillingTableProps> = ({
  rows,
  currentPage,
  totalPages,
  totalItems,
  onPageChange,
  emptyMessage = 'Nenhum segurado encontrado para a pesquisa informada',
  onDeleteSelected,
  onRowClick,
  isDeleting = false,
}) => {
  const {
    closeItemMenu,
    isItemMenuOpen,
    handleItemMenuToggle,
    handleItemMenuKeyDown,
    focusMenuButton,
    registerItemMenuRef,
    registerItemMenuButtonRef,
    registerItemActionRef,
  } = useTableItemMenu<string>();

  const selectableIds = useMemo(
    () => rows.filter(isSeguradoBillingActive).map((row) => row.id),
    [rows],
  );

  const {
    selectedIds,
    allSelected,
    hasSelectedItems,
    handleSelectAll,
    handleSelectItem,
    isSelected,
  } = useTableRowSelection({
    itemIds: selectableIds,
    resetOnItemIdsChange: true,
  });

  const editActionHandlers = createMenuItemActionHandlers<SelfBillingTableItem>({
    onAction: (row) => {
      if (!isSeguradoBillingActive(row)) {
        return;
      }
      onRowClick?.(row);
    },
    closeMenu: closeItemMenu,
    focusMenuButton: (row) => focusMenuButton(row.id),
  });

  const handleDelete = () => {
    if (!hasSelectedItems || isDeleting) {
      return;
    }
    onDeleteSelected?.(Array.from(selectedIds));
  };

  const columnCount = onRowClick ? 9 : 8;

  return (
    <BsdsCard className="bsds-p-16">
      <div className="bsds-display-flex bsds-justify-content-between bsds-align-items-center bsds-mb-16 gap-4">
        <div className="bsds-display-flex bsds-align-items-center">
          <div className="w-[2.5px] h-6 bg-red-600 bsds-mr-12" aria-hidden="true" />
          <h4 className="opacity-80">Resultado da Pesquisa</h4>
        </div>
        {(hasSelectedItems || isDeleting) && (
          <Button
            type="button"
            color="secondary"
            fill="outline"
            icon={iconNavLixeira}
            iconPosition="start"
            onClick={handleDelete}
            disabled={isDeleting}
            loading={isDeleting}
            aria-label={`Excluir ${selectedIds.size} segurado(s) selecionado(s)`}
          >
            Excluir
          </Button>
        )}
      </div>

      <div className="w-full overflow-x-auto custom-scrollbar rounded-md shadow-xs">
        <table className="min-w-[1100px] w-full table-fixed" aria-label="Tabela de segurados do auto faturamento">
          <thead>
            <tr>
              {onRowClick && (
                <th className="px-5 py-3 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap w-16" scope="col">
                  <Tooltip label="Ações">
                    <span>Ações</span>
                  </Tooltip>
                </th>
              )}
              <th className="px-3 py-3 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap w-16" scope="col">
                <Tooltip label="Selecionar todos os segurados ativos da pagina">
                  <BsdsCheckbox
                    checked={allSelected}
                    onBsdsChange={handleSelectAll}
                    size="small"
                    color="secondary"
                    aria-label="Selecionar todos os segurados ativos da pagina"
                  />
                </Tooltip>
              </th>
              <th className="px-5 py-3 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap w-28" scope="col">Matrícula</th>
              <th className="px-5 py-3 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap w-28" scope="col">Tipo Segurado</th>
              <th className="px-6 py-3 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap w-72" scope="col">Nome</th>
              <th className="px-5 py-3 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap w-40" scope="col">Tipo Documento</th>
              <th className="px-5 py-3 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap w-28" scope="col">CPF</th>
              <th className="px-5 py-3 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap w-24" scope="col">Status</th>
              <th className="px-5 py-3 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap" scope="col">Certificado</th>
            </tr>
          </thead>

          <tbody className="bg-white divide-y divide-gray-50 text-text-color">
            {rows.length > 0 ? (
              rows.map((row) => {
                const tipoDocumentoLabel = mapTipoDocumentoToLabel(row.tipoDocumentoCodigo || row.tipoDocumento);
                const isActive = isSeguradoBillingActive(row);

                return (
                  <tr
                    key={row.id}
                    className="hover:bg-gray-50 transition-colors text-xs"
                    aria-selected={isSelected(row.id)}
                  >
                    {onRowClick && (
                      <td className="px-5 py-3 whitespace-nowrap" onClick={(event) => event.stopPropagation()}>
                        <TableItemActionsMenu
                          itemId={row.id}
                          isOpen={isItemMenuOpen(row.id) && isActive}
                          menuId={`segurado-menu-${row.id}`}
                          triggerLabel={
                            isActive
                              ? `Ações do Segurado ${row.nome}`
                              : `Ações indisponíveis para segurado inativo ${row.nome}`
                          }
                          menuLabel={`Menu de Ações do Segurado ${row.nome}`}
                          triggerTooltip={
                            isActive
                              ? `Ações do Segurado ${row.nome}`
                              : 'Editar e excluir indisponíveis para Segurado inativo'
                          }
                          disabled={!isActive}
                          buttonClassName={`rounded p-1 transition-colors focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-gray-500 ${isActive
                            ? 'hover:bg-gray-100 cursor-pointer'
                            : 'cursor-not-allowed opacity-50'
                            }`}
                          onToggle={(event) => handleItemMenuToggle(row.id, event)}
                          onKeyDown={(event) => handleItemMenuKeyDown(row.id, event)}
                          registerMenuRef={(element) => registerItemMenuRef(row.id, element)}
                          registerButtonRef={(element) => registerItemMenuButtonRef(row.id, element)}
                        >
                          <TableMenuItemButton
                            registerRef={(element) => registerItemActionRef(row.id, element)}
                            disabled={!isActive}
                            onClick={(event) => editActionHandlers.handleClick(row, event)}
                            onKeyDown={(event) => editActionHandlers.handleKeyDown(row, event)}
                          >
                            Editar
                          </TableMenuItemButton>
                        </TableItemActionsMenu>
                      </td>
                    )}
                    <td className="px-3 py-3 whitespace-nowrap text-left" onClick={(e) => e.stopPropagation()}>
                      <Tooltip label={
                        isActive
                          ? `Selecionar segurado ${row.nome}`
                          : `Segurado inativo ${row.nome} não pode ser excluído`
                      }
                      >
                        <span className={isActive ? undefined : 'opacity-50 cursor-not-allowed inline-flex'}>
                          <BsdsCheckbox
                            checked={isSelected(row.id)}
                            onBsdsChange={() => {
                              if (!isActive) {
                                return;
                              }
                              handleSelectItem(row.id);
                            }}
                            size="small"
                            color="secondary"
                            aria-label={
                              isActive
                                ? `Selecionar segurado ${row.nome}`
                                : `Segurado inativo ${row.nome} não pode ser selecionado para exclusão`
                            }
                            aria-disabled={!isActive}
                          />
                        </span>
                      </Tooltip>
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap">{row.matricula}</td>
                    <td className="px-5 py-3 whitespace-nowrap">
                      {mapTipoSeguradoToLabel(row.tipoSeguradoCodigo || row.tipoSegurado)}
                    </td>
                    <td className="px-6 py-3 whitespace-nowrap">
                      <Tooltip label={row.nome}>
                        <span>{row.nome}</span>
                      </Tooltip>
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap">
                      <Tooltip label={tipoDocumentoLabel}>
                        <span>{tipoDocumentoLabel}</span>
                      </Tooltip>
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap">{row.cpf}</td>
                    <td className="px-5 py-3 whitespace-nowrap">{row.status}</td>
                    <td className="px-5 py-3 whitespace-nowrap">{row.certificado}</td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={columnCount} className="px-4 py-4 text-center text-text-color text-xs" role="status" aria-live="polite">
                  {emptyMessage}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {totalItems > 0 && (
        <div className="bsds-mt-16 bsds-mb-16">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={onPageChange}
            totalItems={totalItems}
            itemLabel="Segurados"
            alwaysShow
          />
        </div>
      )}
    </BsdsCard>
  );
};

export default SelfBillingTable;
